import { createContext, useContext, Children, cloneElement, isValidElement, type ReactNode, type ReactElement } from 'react';
import { useControllableState } from '../../hooks/useControllableState';
import type { RadioProps } from './Radio';

type RadioGroupContextValue = {
  name: string;
  selectedValue: string;
  disabled: boolean;
  onSelect: (value: string) => void;
};

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export function useRadioGroupContext(): RadioGroupContextValue | null {
  return useContext(RadioGroupContext);
}

/** Groups Radio children under one name, managing the selected value. Controlled and uncontrolled. */
export type RadioGroupProps = {
  /** Shared HTML name attribute for all radios in the group. */
  name: string;
  /** Controlled selected value. */
  value?: string;
  /** Initial value for uncontrolled mode. */
  defaultValue?: string;
  /** Fires when the selected value changes. */
  onChange?: (value: string) => void;
  /** Disables every radio in the group. */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

export function RadioGroup({
  name,
  value: valueProp,
  defaultValue = '',
  onChange,
  disabled = false,
  className = '',
  children,
}: RadioGroupProps) {
  const [selectedValue, setSelectedValue] = useControllableState(valueProp, defaultValue, onChange);

  const onSelect = (val: string) => {
    setSelectedValue(val);
  };

  const ctx: RadioGroupContextValue = { name, selectedValue, disabled, onSelect };

  const enhancedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const radioChild = child as ReactElement<RadioProps>;
    const childValue = radioChild.props.value ?? '';
    return cloneElement(radioChild, {
      name,
      value: childValue,
      status: childValue === selectedValue ? 'active' : 'inactive',
      disabled: disabled || radioChild.props.disabled,
      onChange: () => onSelect(childValue),
    } as Partial<RadioProps>);
  });

  return (
    <RadioGroupContext.Provider value={ctx}>
      <div className={className} role="radiogroup">
        {enhancedChildren}
      </div>
    </RadioGroupContext.Provider>
  );
}
